import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
    DollarSign, 
    Users, 
    TrendingUp, 
    Coffee, 
    Phone, 
    Calendar,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Star,
    ArrowUpRight,
    ArrowDownRight,
    Minus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_brew-business-value/artifacts/t1741byz_Colour%20Landscape%20Logo.png";

const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0
    }).format(value);
};

// Score Gauge Component
const ScoreGauge = ({ score, maxScore = 100 }) => {
    const [animatedScore, setAnimatedScore] = useState(0);
    const percentage = (score / maxScore) * 100;
    
    useEffect(() => {
        const timer = setTimeout(() => setAnimatedScore(score), 300);
        return () => clearTimeout(timer);
    }, [score]);

    const getScoreColor = () => {
        if (score >= 80) return "#22c55e"; // green
        if (score >= 60) return "#F26522"; // orange (secondary)
        if (score >= 40) return "#eab308"; // yellow
        return "#ef4444"; // red
    };

    const radius = 80;
    const strokeWidth = 12;
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = normalizedRadius * Math.PI; // Half circle

    return (
        <div className="relative flex flex-col items-center">
            <svg height={radius + 20} width={radius * 2 + 20} className="overflow-visible">
                {/* Background arc */}
                <path
                    d={`M ${strokeWidth / 2 + 10} ${radius + 10} A ${normalizedRadius} ${normalizedRadius} 0 0 1 ${radius * 2 - strokeWidth / 2 + 10} ${radius + 10}`}
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                />
                {/* Animated progress arc */}
                <motion.path
                    d={`M ${strokeWidth / 2 + 10} ${radius + 10} A ${normalizedRadius} ${normalizedRadius} 0 0 1 ${radius * 2 - strokeWidth / 2 + 10} ${radius + 10}`}
                    fill="none"
                    stroke={getScoreColor()}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    initial={{ strokeDashoffset: circumference }}
                    animate={{ strokeDashoffset: circumference - (percentage / 100) * circumference }}
                    transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
                />
            </svg>
            
            {/* Score Display */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
                <motion.span 
                    className="text-4xl font-bold text-gray-900 font-heading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                >
                    {animatedScore}
                </motion.span>
                <span className="text-lg text-text-muted">/ {maxScore}</span>
            </div>
        </div>
    );
};

// Score Breakdown Item
const ScoreBreakdownItem = ({ label, score, maxScore, delay }) => (
    <motion.div
        className="space-y-2"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay }}
    >
        <div className="flex justify-between text-sm">
            <span className="text-text-secondary">{label}</span>
            <span className="font-medium text-gray-900">{score}/{maxScore}</span>
        </div>
        <Progress 
            value={(score / maxScore) * 100} 
            className="h-2"
        />
    </motion.div>
);

// Comparison Row Component
const ComparisonRow = ({ label, userValue, benchmarkValue, isRange = false, benchmarkLow, benchmarkHigh }) => {
    const getUserVariance = () => {
        if (isRange) {
            const benchmarkMid = (benchmarkLow + benchmarkHigh) / 2;
            if (benchmarkMid === 0) return { status: 'neutral', percent: 0 };
            const variance = ((userValue - benchmarkMid) / benchmarkMid) * 100;
            if (variance > 10) return { status: 'above', percent: Math.round(variance) };
            if (variance < -10) return { status: 'below', percent: Math.round(Math.abs(variance)) };
            return { status: 'neutral', percent: 0 };
        }
        return { status: 'neutral', percent: 0 };
    };

    const variance = getUserVariance();

    return (
        <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100 last:border-b-0">
            <div className="font-medium text-gray-700">{label}</div>
            <div className="text-center">
                <span className="font-semibold text-gray-900">{userValue}</span>
                {variance.status !== 'neutral' && (
                    <span className={`ml-2 text-xs inline-flex items-center ${
                        variance.status === 'above' ? 'text-green-600' : 'text-amber-600'
                    }`}>
                        {variance.status === 'above' ? (
                            <ArrowUpRight className="w-3 h-3" />
                        ) : (
                            <ArrowDownRight className="w-3 h-3" />
                        )}
                        {variance.percent}%
                    </span>
                )}
            </div>
            <div className="text-center text-text-secondary">
                {isRange ? `${benchmarkLow} – ${benchmarkHigh}` : benchmarkValue}
            </div>
        </div>
    );
};

const ResultsScreen = ({ result, leadInfo, onRestart }) => {
    const getExplanation = () => {
        const score = result.benchmark_score;
        if (score >= 80) {
            return "Your café performs exceptionally well against industry benchmarks. Strong customer traffic, efficient operations, and healthy profitability support a premium valuation multiple.";
        } else if (score >= 70) {
            return "Your café performs above industry benchmarks with solid fundamentals. This supports a healthy valuation multiple with potential for growth.";
        } else if (score >= 60) {
            return "Your café performs at industry average levels. There may be opportunities to improve operational efficiency or profitability to increase valuation.";
        } else {
            return "Your café has room for improvement in key operational areas. Focus on increasing profitability and efficiency to boost your business value.";
        }
    };

    const handleCallAgent = () => {
        window.location.href = "tel:1300266922";
    };

    const handleBookConsultation = () => {
        window.open("https://calendly.com/bonza-business-and-franchise-sales/sell-my-cafe?month=2026-03", "_blank");
    };

    return (
        <div className="min-h-screen bg-surface" data-testid="results-screen">
            {/* Header */}
            <header className="w-full py-4 px-6 bg-white border-b border-gray-100">
                <div className="max-w-5xl mx-auto flex items-center justify-between">
                    <img 
                        src={LOGO_URL} 
                        alt="Bonza" 
                        className="h-10 object-contain"
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onRestart}
                        className="flex items-center gap-2"
                        data-testid="restart-btn"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Start Over
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
                {/* Valuation Hero Section */}
                <motion.div
                    className="bg-white rounded-2xl p-8 shadow-card border border-gray-100 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                        <CheckCircle className="w-4 h-4" />
                        <span>Valuation Complete</span>
                    </div>
                    
                    <h1 className="font-heading text-lg text-text-secondary mb-2">
                        Estimated Business Value
                    </h1>
                    
                    <motion.div
                        className="mb-4"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <span className="text-4xl md:text-5xl lg:text-6xl font-bold gradient-text font-heading">
                            {formatCurrency(result.valuation_low)} – {formatCurrency(result.valuation_high)}
                        </span>
                    </motion.div>
                    
                    <p className="text-text-muted text-sm">
                        {result.is_asset_based 
                            ? "Based on asset valuation due to low profitability"
                            : "Based on café industry sales and your business performance"
                        }
                    </p>
                    
                    {result.is_asset_based && (
                        <div className="mt-4 flex items-center justify-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-3">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">Asset-based valuation applied due to low adjusted profit</span>
                        </div>
                    )}
                    
                    {result.asset_valuation_applicable && !result.is_asset_based && (
                        <motion.div 
                            className="mt-6 p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl border-2 border-purple-200"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <h3 className="font-heading text-lg font-semibold text-purple-900 mb-2">
                                Alternative Asset-Based Valuation
                            </h3>
                            <div className="text-3xl font-bold text-purple-700 mb-3">
                                {formatCurrency(result.asset_based_valuation)}
                            </div>
                            <p className="text-sm text-gray-700 mb-3">
                                Due to your high fit-out value ({formatCurrency(result.asset_original_value)} original, 
                                depreciated to {formatCurrency(result.asset_depreciated_value)} over {result.asset_depreciation_years} years at 10% p.a.), 
                                an asset-based approach may yield a higher value.
                            </p>
                            <div className="flex items-center gap-2 text-purple-800 bg-purple-100 rounded-lg p-3">
                                <Phone className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                    We recommend speaking with a broker to discuss the best approach for your sale.
                                </span>
                            </div>
                        </motion.div>
                    )}
                </motion.div>

                {/* Score and Metrics Grid */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Benchmark Score Card */}
                    <motion.div
                        className="lg:col-span-1 bg-white rounded-2xl p-6 shadow-card border border-gray-100"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                    >
                        <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4 text-center">
                            Café Performance Score
                        </h2>
                        
                        <ScoreGauge score={result.benchmark_score} />
                        
                        <div className="mt-6 text-center">
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold">
                                <Star className="w-4 h-4" />
                                {result.performance_category}
                            </span>
                        </div>
                    </motion.div>

                    {/* Key Metrics */}
                    <div className="lg:col-span-2 space-y-4">
                        <h2 className="font-heading text-lg font-semibold text-gray-900">
                            Valuation Metrics
                        </h2>
                        
                        <div className="grid sm:grid-cols-2 gap-4">
                            <motion.div
                                className="bg-white rounded-xl p-5 border border-gray-100 shadow-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.2 }}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-text-muted mb-1">Owner Adjusted Profit</p>
                                        <p className="text-xl font-bold text-gray-900">{formatCurrency(result.owner_adjusted_profit)}</p>
                                        <p className="text-xs text-text-muted mt-1">Normalized for 40hr/week owner</p>
                                    </div>
                                </div>
                            </motion.div>
                            
                            <motion.div
                                className="bg-white rounded-xl p-5 border border-gray-100 shadow-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.3 }}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                        <Coffee className="w-5 h-5 text-secondary" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-text-muted mb-1">Valuation Multiple</p>
                                        <p className="text-xl font-bold text-gray-900">{result.adjusted_multiple_low}x – {result.adjusted_multiple_high}x</p>
                                        <p className="text-xs text-text-muted mt-1">Base: {result.base_multiple_low}x – {result.base_multiple_high}x</p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* Your Inputs vs Benchmarks Comparison */}
                <motion.div
                    className="bg-white rounded-2xl p-6 shadow-card border border-gray-100"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.25 }}
                >
                    <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4">
                        Your Inputs vs Industry Benchmarks
                    </h2>
                    <p className="text-sm text-text-muted mb-4">
                        Benchmarks calculated from your coffee bean usage ({result.user_coffee_kg}kg/week)
                    </p>
                    
                    <div className="overflow-x-auto">
                        <div className="min-w-[400px]">
                            {/* Header */}
                            <div className="grid grid-cols-3 gap-4 py-3 border-b-2 border-gray-200 font-semibold text-sm">
                                <div className="text-gray-600">Metric</div>
                                <div className="text-center text-primary">Your Input</div>
                                <div className="text-center text-gray-600">Benchmark (±10%)</div>
                            </div>
                            
                            {/* Coffee Usage */}
                            <ComparisonRow 
                                label="Coffee Beans (kg/week)"
                                userValue={`${result.user_coffee_kg} kg`}
                                benchmarkValue="Your baseline"
                            />
                            
                            {/* Revenue */}
                            <ComparisonRow 
                                label="Annual Revenue"
                                userValue={formatCurrency(result.user_revenue)}
                                isRange={true}
                                benchmarkLow={formatCurrency(result.benchmark_revenue_low)}
                                benchmarkHigh={formatCurrency(result.benchmark_revenue_high)}
                            />
                            
                            {/* Customers */}
                            <ComparisonRow 
                                label="Customers/Day"
                                userValue={`${result.user_customers}`}
                                benchmarkValue={`~${result.benchmark_customers}`}
                            />
                            
                            {/* Owner Adjusted Profit */}
                            <ComparisonRow 
                                label="Owner Adjusted Profit"
                                userValue={formatCurrency(result.user_owner_adjusted_profit)}
                                isRange={true}
                                benchmarkLow={formatCurrency(result.benchmark_profit_low)}
                                benchmarkHigh={formatCurrency(result.benchmark_profit_high)}
                            />
                        </div>
                    </div>
                </motion.div>

                {/* Variance Commentary */}
                {result.variance_commentary && result.variance_commentary.length > 0 && (
                    <motion.div
                        className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-6 border border-primary/10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                    >
                        <h2 className="font-heading text-lg font-semibold text-gray-900 mb-3">
                            Analysis Commentary
                        </h2>
                        <ul className="space-y-2">
                            {result.variance_commentary.map((comment, index) => (
                                <li key={index} className="flex items-start gap-2 text-text-secondary">
                                    <Minus className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                                    <span>{comment}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                )}

                {/* Score Breakdown */}
                <motion.div
                    className="bg-white rounded-2xl p-6 shadow-card border border-gray-100"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.35 }}
                >
                    <h2 className="font-heading text-lg font-semibold text-gray-900 mb-6">
                        Score Breakdown
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <ScoreBreakdownItem 
                                label="Profitability" 
                                score={result.profitability_score} 
                                maxScore={35}
                                delay={0.4}
                            />
                            <ScoreBreakdownItem 
                                label="Revenue Scale" 
                                score={result.revenue_scale_score} 
                                maxScore={20}
                                delay={0.5}
                            />
                            <ScoreBreakdownItem 
                                label="Operational Efficiency" 
                                score={result.efficiency_score} 
                                maxScore={15}
                                delay={0.6}
                            />
                        </div>
                        <div className="space-y-4">
                            <ScoreBreakdownItem 
                                label="Customer Traffic" 
                                score={result.traffic_score} 
                                maxScore={20}
                                delay={0.7}
                            />
                            <ScoreBreakdownItem 
                                label="Business Stability" 
                                score={result.stability_score} 
                                maxScore={10}
                                delay={0.8}
                            />
                        </div>
                    </div>
                </motion.div>

                {/* Benchmark Explanation */}
                <motion.div
                    className="bg-gray-50 rounded-2xl p-6 border border-gray-200"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                >
                    <h2 className="font-heading text-lg font-semibold text-gray-900 mb-3">
                        Performance Summary
                    </h2>
                    <p className="text-text-secondary leading-relaxed">
                        {getExplanation()}
                    </p>
                </motion.div>

                {/* Validation Notes */}
                {result.validation_notes && result.validation_notes.length > 0 && (
                    <motion.div
                        className="bg-amber-50 rounded-2xl p-6 border border-amber-200"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.45 }}
                    >
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-amber-800 mb-2">Notes</h3>
                                <ul className="space-y-1">
                                    {result.validation_notes.map((note, index) => (
                                        <li key={index} className="text-sm text-amber-700">• {note}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* CTA Section */}
                <motion.div
                    className="bg-white rounded-2xl p-8 shadow-card border border-gray-100 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                >
                    {/* Bonza Logo */}
                    <img 
                        src={LOGO_URL}
                        alt="Bonza Business & Franchise Sales"
                        className="h-14 mx-auto mb-6 object-contain"
                    />
                    
                    <h2 className="font-heading text-2xl font-bold text-gray-900 mb-3">
                        Ready to Sell Your Café?
                    </h2>
                    <p className="text-text-secondary mb-6 max-w-lg mx-auto">
                        Get expert guidance from Australia&apos;s most trusted small business sales specialists. 
                        Fair 4% commission. Full support from listing to settlement.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button
                            size="lg"
                            className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/30 transition-all duration-300 hover:-translate-y-0.5 rounded-xl px-8 py-6 font-semibold group w-full sm:w-auto"
                            onClick={handleCallAgent}
                            data-testid="cta-call-agent"
                        >
                            <Phone className="w-5 h-5 mr-2" />
                            Call our Business Sales Consultants
                        </Button>
                        
                        <Button
                            variant="outline"
                            size="lg"
                            className="border-2 border-primary/20 hover:border-primary/50 hover:bg-primary/5 rounded-xl px-8 py-6 font-medium w-full sm:w-auto"
                            onClick={handleBookConsultation}
                            data-testid="cta-book-consultation"
                        >
                            <Calendar className="w-5 h-5 mr-2" />
                            Book a Free Consultation
                        </Button>
                    </div>
                    
                    <p className="text-sm text-text-muted mt-4">
                        Call us on <a href="tel:1300266922" className="text-primary font-medium hover:underline">1300 266 922</a>
                    </p>
                </motion.div>

                {/* Confidence Note */}
                <motion.div
                    className="text-center text-sm text-text-muted"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                >
                    <p>
                        This valuation is an estimate based on the information provided and industry benchmarks. 
                        For a detailed appraisal, please contact Bonza Business & Franchise Sales.
                    </p>
                </motion.div>
            </main>

            {/* Footer */}
            <footer className="w-full py-6 px-6 bg-white border-t border-gray-100 mt-8">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-text-muted">
                    <p>© 2026 Bonza Business & Franchise Sales</p>
                    <a 
                        href="https://bonza.com.au" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="hover:text-primary transition-colors"
                    >
                        Visit Bonza.com.au
                    </a>
                </div>
            </footer>
        </div>
    );
};

export default ResultsScreen;
